import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Instagram, 
  Mail, 
  Heart
} from 'lucide-react';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import InlineEditor from '../ui/InlineEditor';
import InlineTextArea from '../ui/InlineTextArea';
import InlineUrlEditor from '../ui/InlineUrlEditor';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { getSetting, getLocalizedSetting } = useSiteSettings();

  return (
    <footer className="bg-gray-900 border-t border-gray-700">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  <InlineEditor settingKey="footer_company_name" defaultValue="TOLOU">
                    {getSetting('footer_company_name', 'TOLOU')}
                  </InlineEditor>
                </h3>
              </div>
            </div>
            <p className="text-gray-300 text-sm">
              <InlineTextArea settingKey="footer_description_fr" defaultValue="Bijoux élégants avec des éléments iraniens. Chaque pièce raconte une histoire de tradition et d'élégance." rows={2}>
                {getLocalizedSetting('footer_description', 'Bijoux élégants avec des éléments iraniens. Chaque pièce raconte une histoire de tradition et d\'élégance.')}
              </InlineTextArea>
            </p>
            <div className="flex space-x-4">
              <InlineUrlEditor 
                settingKey="footer_instagram_url" 
                defaultValue="https://instagram.com/toloujewelry"
                className="text-gray-400 hover:text-yellow-500 transition-colors duration-200 cursor-pointer"
                iconComponent={<Instagram size={20} />}
              />
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">
              <InlineEditor settingKey="footer_quick_links_title_fr" defaultValue="Liens Rapides">
                Liens Rapides
              </InlineEditor>
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/products" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_products_fr" defaultValue="Nos Produits">
                    {getLocalizedSetting('nav_products', 'Nos Produits')}
                  </InlineEditor>
                </Link>
              </li>
              <li>
                <Link to="/categories" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_categories_fr" defaultValue="Catégories">
                    {getLocalizedSetting('nav_categories', 'Catégories')}
                  </InlineEditor>
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_about_fr" defaultValue="À Propos">
                    {getLocalizedSetting('nav_about', 'À Propos')}
                  </InlineEditor>
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_contact_fr" defaultValue="Contact">
                    {getLocalizedSetting('nav_contact', 'Contact')}
                  </InlineEditor>
                </Link>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">
              <InlineEditor settingKey="footer_customer_service_title_fr" defaultValue="Service Client">
                Service Client
              </InlineEditor>
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/profile" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_profile_fr" defaultValue="Mon Compte">
                    {getLocalizedSetting('nav_profile', 'Mon Compte')}
                  </InlineEditor>
                </Link>
              </li>
              <li>
                <Link to="/orders" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_orders_fr" defaultValue="Mes Commandes">
                    {getLocalizedSetting('nav_orders', 'Mes Commandes')}
                  </InlineEditor>
                </Link>
              </li>
              <li>
                <Link to="/wishlist" className="text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                  <InlineEditor settingKey="nav_wishlist_fr" defaultValue="Liste de Souhaits">
                    {getLocalizedSetting('nav_wishlist', 'Liste de Souhaits')}
                  </InlineEditor>
                </Link>
              </li>
              {null}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white">
              <InlineEditor settingKey="footer_contact_title_fr" defaultValue="Contact">
                {getLocalizedSetting('footer_contact_title', 'Contact')}
              </InlineEditor>
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="text-yellow-500" size={18} />
                <div className="text-gray-300 text-sm">
                  <InlineEditor settingKey="footer_contact_email" defaultValue="contact@tolou-jewelry.com">
                    {getSetting('footer_contact_email', 'contact@tolou-jewelry.com')}
                  </InlineEditor>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <Heart className="text-red-500" size={16} />
              <span>
                <InlineTextArea settingKey="footer_made_with_fr" defaultValue="Fait avec ❤️ en France avec des éléments iraniens" rows={1}>
                  {getLocalizedSetting('footer_made_with', 'Fait avec ❤️ en France avec des éléments iraniens')}
                </InlineTextArea>
              </span>
            </div>
            <div className="text-gray-400 text-sm">
              <InlineEditor settingKey="footer_copyright_fr" defaultValue={`© ${currentYear} TOLOU Jewelry. Tous droits réservés.`}>
                {getSetting('footer_copyright', `© ${currentYear} TOLOU Jewelry. Tous droits réservés.`)}
              </InlineEditor>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 